import { useQueryClient } from '@tanstack/react-query';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIoContext, useOverlayContext } from "../../../../../../contexts";
import { QueryKeys } from "../../../../../../hooks/reactQuery/queryKeys";
import { useMutationState } from "../../../../../../hooks/reactQuery/useMutationState";
import { sendErrReport } from "../../../../../../infra/utils";
import { legalTermsModel } from "../../../../../../models";
import { useAppConfig, useStoreActions } from "../../../../../../store/store-context";
export let UseCreateLegalTermsErrorToastId = /*#__PURE__*/function (UseCreateLegalTermsErrorToastId) {
  UseCreateLegalTermsErrorToastId["Unknown"] = "unknown";
  UseCreateLegalTermsErrorToastId["InvalidPDF"] = "invalid-pdf";
  UseCreateLegalTermsErrorToastId["TooLarge"] = "too-large";
  return UseCreateLegalTermsErrorToastId;
}({});
export class PDFError extends Error {
  constructor(message, id) {
    super(message);
    this.id = id;
  }
}
export const {
  UseCreateLegalTermsTID
} = getTestIds();
export const useCreateLegalTerms = _ref => {
  let {
    onSettled
  } = _ref;
  const {
    api
  } = useStoreActions();
  const queryClient = useQueryClient();
  const overlayContext = useOverlayContext();
  const {
    termsAndConditionsPDFSizeLimitInMegabytes: pdfSizeLimit,
    generateUniqueId
  } = useAppConfig();
  const {
    uploadFileToCloudStorage
  } = useIoContext();
  const {
    mutate: createLegalTerms,
    isLoading
  } = useMutationState({
    mutationFn: async file => {
      if (!file) {
        throw new PDFError('An uknown error has occurred.', UseCreateLegalTermsErrorToastId.Unknown);
      }
      if (file.type !== 'application/pdf') {
        throw new PDFError('File is not a valid PDF.', UseCreateLegalTermsErrorToastId.InvalidPDF);
      }
      if (file.size > pdfSizeLimit * 1024 * 1024) {
        throw new PDFError('Selected file is too large. Please select a smaller one.', UseCreateLegalTermsErrorToastId.TooLarge);
      }
      const {
        fileId,
        url
      } = await api.business.getLegalTermsUploadURL();
      await uploadFileToCloudStorage(url, file, {
        'content-type': 'application/pdf'
      });
      const uploadedLegalTerms = legalTermsModel.makePdfLegalTerms({
        name: file.name,
        pdfUniqueName: fileId
      }, generateUniqueId);
      await api.business.createLegalTerms(uploadedLegalTerms);
      return uploadedLegalTerms;
    },
    onSuccess: async uploadedLegalTerms => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.LegalTerms]
      });
      onSettled(uploadedLegalTerms);
    },
    onError: error => {
      const isPdfError = error instanceof PDFError;
      const message = isPdfError ? error.message : 'An unknown error has occurred.';
      if (!isPdfError) {
        sendErrReport(error);
      }
      const id = isPdfError ? error.id : UseCreateLegalTermsErrorToastId.Unknown;
      overlayContext.showErrorToast(message, UseCreateLegalTermsTID.ErrorToast(id));
      onSettled(undefined);
    }
  });
  return {
    createLegalTerms,
    isLoading
  };
};